import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNELS = [
  { value: 'facebook', label: 'Facebook / Marketplace' },
  { value: 'phone', label: 'Phone call' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
  { value: 'in_person', label: 'In person' },
];

export const QuickEnquiryModal = ({ open, onClose, onSaved, itemId, itemTitle, preselectedBuyer }) => {
  const [buyerSearch, setBuyerSearch] = useState('');
  const [buyerResults, setBuyerResults] = useState([]);
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', facebook_profile: '' });
  const [type, setType] = useState('enquiry');
  const [channel, setChannel] = useState('facebook');
  const [offerAmount, setOfferAmount] = useState('');
  const [message, setMessage] = useState('');
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(itemId || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedBuyer(preselectedBuyer || null);
    setBuyerSearch('');
    setForm({ name: '', phone: '', email: '', facebook_profile: '' });
    setType('enquiry');
    setChannel('facebook');
    setOfferAmount('');
    setMessage('');
    setSelectedItemId(itemId || '');
    if (!itemId) {
      axios.get(`${API}/items?limit=200`, { withCredentials: true })
        .then(res => setItems(res.data || []))
        .catch(() => setItems([]));
    }
  }, [open, itemId, preselectedBuyer]);

  const searchBuyers = useCallback(async (q) => {
    if (!q || q.length < 2) { setBuyerResults([]); return; }
    try {
      const { data } = await axios.get(`${API}/buyers?search=${encodeURIComponent(q)}&limit=8`, { withCredentials: true });
      setBuyerResults(data || []);
    } catch (e) { setBuyerResults([]); }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => searchBuyers(buyerSearch), 250);
    return () => clearTimeout(t);
  }, [buyerSearch, searchBuyers]);

  const submit = async () => {
    if (!selectedBuyer && !form.name.trim()) { toast.error('Enter the buyer name or pick an existing buyer'); return; }
    if (!selectedItemId) { toast.error('Select the item'); return; }
    if (type === 'offer' && !offerAmount) { toast.error('Enter the offer amount'); return; }
    try {
      setSaving(true);
      const payload = {
        item_id: selectedItemId,
        type,
        channel,
        message: message.trim() || null,
        offer_amount: type === 'offer' ? parseFloat(offerAmount) : null,
        ...(selectedBuyer ? { buyer_id: selectedBuyer.id } : {
          buyer_name: form.name.trim(),
          buyer_phone: form.phone.trim() || null,
          buyer_email: form.email.trim() || null,
          facebook_profile: form.facebook_profile.trim() || null,
        })
      };
      const { data } = await axios.post(`${API}/buyers/enquiries`, payload, { withCredentials: true });
      toast.success(data.new_buyer ? 'Enquiry logged — new buyer added to CRM' : 'Enquiry logged');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to log enquiry');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quick-enquiry-modal">
        <DialogHeader>
          <DialogTitle>Log Enquiry {itemTitle ? `— ${itemTitle}` : ''}</DialogTitle>
          <DialogDescription className="sr-only">Capture a buyer enquiry, offer or inspection request</DialogDescription>
        </DialogHeader>

        {/* Buyer */}
        {selectedBuyer ? (
          <div className="flex items-center justify-between p-3 rounded-lg bg-[#F7FBEC] border border-[#C6E76C]" data-testid="selected-buyer-chip">
            <div>
              <p className="text-sm font-semibold">{selectedBuyer.name} {selectedBuyer.vip && '⭐'}</p>
              <p className="text-xs text-gray-500">{selectedBuyer.phone || selectedBuyer.email || 'no contact'} • {selectedBuyer.enquiry_count || 0} enquiries</p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setSelectedBuyer(null)} data-testid="change-buyer-button">Change</Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Input
              placeholder="Search existing buyers (name / phone / email)…"
              value={buyerSearch}
              onChange={(e) => setBuyerSearch(e.target.value)}
              data-testid="buyer-search-input"
            />
            {buyerResults.length > 0 && (
              <div className="border rounded-md divide-y max-h-36 overflow-y-auto">
                {buyerResults.map(b => (
                  <button key={b.id} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                    onClick={() => { setSelectedBuyer(b); setBuyerResults([]); }} data-testid={`buyer-result-${b.id}`}>
                    <span className="font-medium">{b.name}</span> {b.vip && '⭐'}
                    <span className="text-xs text-gray-400 ml-2">{b.phone || b.email}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="Buyer name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="buyer-name-input" />
              <Input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="buyer-phone-input" />
              <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="buyer-email-input" />
              <Input placeholder="Facebook profile / name" value={form.facebook_profile} onChange={(e) => setForm({ ...form, facebook_profile: e.target.value })} data-testid="buyer-fb-input" />
            </div>
          </div>
        )}

        {/* Item */}
        {!itemId && (
          <Select value={selectedItemId} onValueChange={setSelectedItemId}>
            <SelectTrigger className="w-full" data-testid="enquiry-item-select">
              <SelectValue placeholder="Select item…" />
            </SelectTrigger>
            <SelectContent>
              {items.map(it => <SelectItem key={it.id} value={it.id}>{it.title} ({it.reference_code})</SelectItem>)}
            </SelectContent>
          </Select>
        )}

        {/* Type */}
        <div className="grid grid-cols-3 gap-2">
          {['enquiry', 'offer', 'inspection'].map(t => (
            <button key={t} onClick={() => setType(t)}
              className={`px-3 py-2 rounded-md text-sm font-medium border capitalize ${type === t ? 'bg-[#1E2117] text-[#C6E76C] border-[#1E2117]' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              data-testid={`enquiry-type-${t}`}>
              {t}
            </button>
          ))}
        </div>
        {type === 'offer' && (
          <Input type="number" min="0" placeholder="Offer amount ($)" value={offerAmount} onChange={(e) => setOfferAmount(e.target.value)} data-testid="offer-amount-input" />
        )}

        {/* Channel + message */}
        <Select value={channel} onValueChange={setChannel}>
          <SelectTrigger className="w-full" data-testid="enquiry-channel-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CHANNELS.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Textarea placeholder="Message / what they said (optional)" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} data-testid="enquiry-message-input" />

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="enquiry-cancel-button">Cancel</Button>
          <Button onClick={submit} disabled={saving}
            className="bg-[#C6E76C] hover:bg-[#b5d65f] text-[#1E2117] font-semibold" data-testid="enquiry-save-button">
            {saving ? 'Saving…' : 'Log Enquiry'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
